import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import isEqual from 'lodash/isEqual';
import { INITIAL_TEST_CASE_DATA } from '@shared/constants/initialDataConstants';

const testCasesSlice = createSlice({
    name: '@testCases',
    initialState: {
        testCaseHistory: [],
        testCaseSelectedRevision: null,
        testCase: null,
        testCaseLocal: null,
        testCases: [],
        testCasesCount: 0,
        testCasesFromAddDialog: [],
        allTestCases: [],
        getTestCasesStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestCasesError: null,
        getAllTestCasesStatus: REQUEST_STATUS.NOT_REQUESTED,
        getAllTestCasesError: null,
        getTestCaseByIdStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestCaseByIdError: null,
        getTestCaseCertainRevisionStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestCaseCertainRevisionError: null,
        addStatus: REQUEST_STATUS.NOT_REQUESTED,
        addError: null,
        testCaseResults: [],
        testCaseResultsCount: 0,
        getTestCaseResultsStatus: REQUEST_STATUS.NOT_REQUESTED,
        getTestCaseResultsError: null,
        duplicateStatus: REQUEST_STATUS.NOT_REQUESTED,
        duplicateError: null,
    },
    reducers: {
        // READ
        getTestCasesRequest(state) {
            state.getTestCasesStatus = REQUEST_STATUS.LOADING;
            state.getTestCasesError = null;
        },
        getTestCasesSuccess(state, action) {
            state.getTestCasesStatus = REQUEST_STATUS.SUCCESS;
            state.getTestCasesError = null;
            const enableInfiniteScroll = action.payload.enableInfiniteScroll;
            if (enableInfiniteScroll) {
                state.testCases.push(...action.payload.data.items);
            } else {
                state.testCases = action.payload.data.items || [];
            }
            state.testCasesCount = action.payload.data.count;
        },
        getTestCasesError(state, action) {
            state.getTestCasesStatus = REQUEST_STATUS.ERROR;
            state.getTestCasesError = action;
            state.testCases = [];
            state.testCasesCount = 0;
        },
        getTestCasesReset(state) {
            state.getTestCasesStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestCasesError = null;
            state.testCases = [];
            state.testCasesCount = 0;
        },
        resetTestcasesArr(state) {
            state.testCases = [];
        },
        getAllTestCasesRequest(state) {
            state.getAllTestCasesStatus = REQUEST_STATUS.LOADING;
            state.getAllTestCasesError = null;
        },
        getAllTestCasesSuccess(state, action) {
            state.getAllTestCasesStatus = REQUEST_STATUS.SUCCESS;
            state.getAllTestCasesError = null;
            state.allTestCases = action.payload.data.items || [];
        },
        getAllTestCasesError(state, action) {
            state.getAllTestCasesStatus = REQUEST_STATUS.ERROR;
            state.getAllTestCasesError = action;
            state.allTestCases = [];
        },
        getAllTestCasesReset(state) {
            state.getAllTestCasesStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getAllTestCasesError = null;
            state.allTestCases = [];
        },

        setDefaultTestcaseData(state) {
            state.getTestCaseByIdStatus = REQUEST_STATUS.SUCCESS;
            state.getTestCaseByIdError = null;
            state.testCase = INITIAL_TEST_CASE_DATA;
            state.testCaseLocal = INITIAL_TEST_CASE_DATA;
        },
        getTestCaseByIdRequest(state) {
            state.getTestCaseByIdStatus = REQUEST_STATUS.LOADING;
            state.getTestCaseByIdError = null;
            state.testCase = null;
            state.testCaseLocal = null;
        },
        getTestCaseByIdSuccess(state, action) {
            const data = action.payload.data;
            state.getTestCaseByIdStatus = REQUEST_STATUS.SUCCESS;
            state.getTestCaseByIdError = null;
            state.testCase = data;
            state.testCaseLocal = data;
        },
        getTestCaseByIdError(state, action) {
            state.getTestCaseByIdStatus = REQUEST_STATUS.ERROR;
            state.getTestCaseByIdError = action;
            state.testCase = null;
            state.testCaseLocal = null;
        },
        getTestCaseByIdReset(state) {
            state.getTestCaseByIdStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestCaseByIdError = null;
            state.testCase = null;
            state.testCaseLocal = null;
        },
        updateTestCaseField(state, action) {
            const { field, newValue, updateActualData } = action.payload;
            state.testCaseLocal[field] = newValue;

            if (updateActualData) {
                state.testCase[field] = newValue;
            }
        },
        updateTestCaseStepEditorData(state, action) {
            const { step, kind, newValue } = action.payload;

            const stepToUpdateIndex = state.testCaseLocal['test_scripts'].findIndex((s) => s.id === step.id);

            // update data only if data changes, ignore time of EditorJs update changes
            if (!isEqual(state.testCaseLocal['test_scripts'][stepToUpdateIndex][kind]['blocks'], newValue['blocks'])) {
                state.testCaseLocal['test_scripts'][stepToUpdateIndex][kind] = newValue;
            }
        },
        revertTestCaseLocalChanges(state) {
            state.testCaseLocal = state.testCase;
        },

        getTestCaseResultsRequest(state) {
            state.getTestCaseResultsStatus = REQUEST_STATUS.LOADING;
            state.getTestCaseResultsError = null;
        },
        getTestCaseResultsSuccess(state, action) {
            state.getTestCaseResultsStatus = REQUEST_STATUS.SUCCESS;
            state.getTestCaseResultsError = null;
            state.testCaseResults = action.payload.data.items || [];
            state.testCaseResultsCount = action.payload.data.count;
        },
        getTestCaseResultsError(state, action) {
            state.getTestCaseResultsStatus = REQUEST_STATUS.ERROR;
            state.getTestCaseResultsError = action;
            state.testCaseResults = [];
            state.testCaseResultsCount = 0;
        },
        getTestCaseResultsReset(state) {
            state.getTestCaseResultsStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestCaseResultsError = null;
            state.testCaseResults = [];
            state.testCaseResultsCount = 0;
        },

        // GET REVISIONS HISTORY
        getTestCaseHistoryRequest(state) {
            state.getTestCaseHistoryStatus = REQUEST_STATUS.LOADING;
            state.getTestCaseHistoryError = null;
        },
        getTestCaseHistorySuccess(state, action) {
            const responseData = action.payload.data.items;
            state.getTestCaseHistoryStatus = REQUEST_STATUS.SUCCESS;
            state.getTestCaseHistoryError = null;
            state.testCaseHistory = responseData;
        },
        getTestCaseHistoryError(state, action) {
            state.getTestCaseHistoryStatus = REQUEST_STATUS.ERROR;
            state.getTestCaseHistoryError = action;
            state.testCaseHistory = [];
        },
        getTestCaseHistoryReset(state) {
            state.getTestCaseHistoryStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestCaseHistoryError = null;
            state.testCaseHistory = [];
        },

        // CREATE
        addTestCaseRequest(state) {
            state.addStatus = REQUEST_STATUS.LOADING;
            state.addError = null;
            state.createdTestcaseId = null;
        },
        addTestCaseSuccess(state, action) {
            state.addStatus = REQUEST_STATUS.SUCCESS;
            state.addError = null;

            state.getTestCasesStatus = REQUEST_STATUS.NOT_REQUESTED; // after we add (update, delete) TestCase, need to get the list of TestCase again
            state.getTestCasesError = null;
            state.createdTestcaseId = action.payload.pk;
        },
        addTestCaseError(state, action) {
            state.addStatus = REQUEST_STATUS.ERROR;
            state.addError = action;
            state.createdTestcaseId = null;
        },
        addTestCaseReset(state) {
            state.addStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.addError = null;
            state.createdTestcaseId = null;
        },

        // UPDATE
        editTestCaseRequest(state) {
            state.editStatus = REQUEST_STATUS.LOADING;
            state.editError = null;
        },
        editTestCaseSuccess(state, action) {
            state.editStatus = REQUEST_STATUS.SUCCESS;
            state.editError = null;
            if (action.payload) {
                state.testCase = action.payload;
                state.testCaseLocal = action.payload;
            }
        },
        editTestCaseError(state, action) {
            state.editStatus = REQUEST_STATUS.ERROR;
            state.editError = action;
        },
        editTestCaseReset(state) {
            state.editStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.editError = null;
        },

        // DELETE
        deleteTestCaseRequest(state) {
            state.deleteStatus = REQUEST_STATUS.LOADING;
            state.deleteError = null;
        },
        deleteTestCaseSuccess(state) {
            state.deleteStatus = REQUEST_STATUS.SUCCESS;
            state.deleteError = null;

            state.getTestCasesStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestCasesError = null;
        },
        deleteTestCaseError(state, action) {
            state.deleteStatus = REQUEST_STATUS.ERROR;
            state.deleteError = action;
        },
        deleteTestCaseReset(state) {
            state.deleteStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.deleteError = null;
        },

        // FETCH CERTAIN REVISION FROM TESTCASE HISTORY
        getTestCaseCertainRevisionRequest(state) {
            state.getTestCaseCertainRevisionStatus = REQUEST_STATUS.LOADING;
            state.getTestCaseCertainRevisionError = null;
        },
        getTestCaseCertainRevisionSuccess(state, action) {
            const responseData = action.payload.data;
            state.getTestCaseCertainRevisionStatus = REQUEST_STATUS.SUCCESS;
            state.getTestCaseCertainRevisionError = null;
            state.testCaseSelectedRevision = responseData;
        },
        getTestCaseCertainRevisionError(state, action) {
            state.getTestCaseCertainRevisionStatus = REQUEST_STATUS.ERROR;
            state.getTestCaseCertainRevisionError = action;
            state.testCaseSelectedRevision = null;
        },
        getTestCaseCertainRevisionReset(state) {
            state.getTestCaseCertainRevisionStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.getTestCaseCertainRevisionError = null;
            state.testCaseSelectedRevision = null;
        },
        // PUSH NEW RECORD TO TESTCASE's HISTORY
        checkoutTestCaseRevisionRequest(state) {
            state.checkoutTestCaseRevisionStatus = REQUEST_STATUS.LOADING;
            state.checkoutTestCaseRevisionError = null;
        },
        checkoutTestCaseRevisionSuccess(state) {
            state.checkoutTestCaseRevisionStatus = REQUEST_STATUS.SUCCESS;
            state.checkoutTestCaseRevisionError = null;
        },
        checkoutTestCaseRevisionError(state, action) {
            state.checkoutTestCaseRevisionStatus = REQUEST_STATUS.ERROR;
            state.checkoutTestCaseRevisionError = action;
        },
        checkoutTestCaseRevisionReset(state) {
            state.checkoutTestCaseRevisionStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.checkoutTestCaseRevisionError = null;
        },

        updateTestCasesFromAddDialog(state, action) {
            state.testCasesFromAddDialog = action.payload;
        },

        addToTestcasesFromAddDialog(state, action) {
            const isAlreadyAdded = state.testCasesFromAddDialog.some((testcase) => testcase.pk === action.payload.pk);
            if (!isAlreadyAdded) {
                state.testCasesFromAddDialog.push(action.payload);
            }
        },

        deleteFromTestcasesFromAddDialog(state, action) {
            state.testCasesFromAddDialog = state.testCasesFromAddDialog.filter(
                (testcase) => testcase.pk !== action.payload.pk,
            );
        },

        // DUPLICATE
        duplicateTestCaseRequest(state) {
            state.duplicateStatus = REQUEST_STATUS.LOADING;
            state.duplicateError = null;
        },
        duplicateTestCaseSuccess(state) {
            state.duplicateStatus = REQUEST_STATUS.SUCCESS;
            state.duplicateError = null;
        },
        duplicateTestCaseError(state, action) {
            state.duplicateStatus = REQUEST_STATUS.ERROR;
            state.duplicateError = action.payload;
        },
        duplicateTestCaseReset(state) {
            state.duplicateStatus = REQUEST_STATUS.NOT_REQUESTED;
            state.duplicateError = null;
        },
    },
});

export default testCasesSlice.reducer;

export const {
    getTestCasesRequest,
    getTestCasesSuccess,
    getTestCasesError,
    getTestCasesReset,
    resetTestcasesArr,
    getAllTestCasesRequest,
    getAllTestCasesSuccess,
    getAllTestCasesError,
    getAllTestCasesReset,
    setDefaultTestcaseData,
    getTestCaseByIdRequest,
    getTestCaseByIdSuccess,
    getTestCaseByIdError,
    getTestCaseByIdReset,
    updateTestCaseField,
    updateTestCaseStepEditorData,
    revertTestCaseLocalChanges,
    getTestCaseResultsRequest,
    getTestCaseResultsSuccess,
    getTestCaseResultsError,
    getTestCaseResultsReset,
    getTestCaseHistoryRequest,
    getTestCaseHistorySuccess,
    getTestCaseHistoryError,
    getTestCaseHistoryReset,
    addTestCaseRequest,
    addTestCaseSuccess,
    addTestCaseError,
    addTestCaseReset,
    editTestCaseRequest,
    editTestCaseSuccess,
    editTestCaseError,
    editTestCaseReset,
    deleteTestCaseRequest,
    deleteTestCaseSuccess,
    deleteTestCaseError,
    deleteTestCaseReset,
    getTestCaseCertainRevisionRequest,
    getTestCaseCertainRevisionSuccess,
    getTestCaseCertainRevisionError,
    getTestCaseCertainRevisionReset,
    checkoutTestCaseRevisionRequest,
    checkoutTestCaseRevisionSuccess,
    checkoutTestCaseRevisionError,
    checkoutTestCaseRevisionReset,
    updateTestCasesFromAddDialog,
    addToTestcasesFromAddDialog,
    deleteFromTestcasesFromAddDialog,
    duplicateTestCaseRequest,
    duplicateTestCaseSuccess,
    duplicateTestCaseError,
    duplicateTestCaseReset,
} = testCasesSlice.actions;
